import { createPortal } from "react-dom";
import { Button } from "../components/ui/Button";
import EmptyState from "../components/EmptyState";
import LazyImage from "../components/ui/LazyImage";
import Logo from "../assets/images/green-logo.png";
import NewsSection from "../components/NewsSection";
import { isEmptyContent } from "../utils/isEmptyContent";
import ProjectGallery from "../components/ProjectGallery";
import ProjectFollowBox from "../components/ProjectFollowBox";
import { Link, useParams, useNavigate } from "react-router-dom";
import { mapPersonNewsToNews } from "../utils/mapPersonNewsToNews";
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useSinglePerson } from "../features/home/hooks/useSinglePerson";
import { useFollowUser } from "../features/managers/hooks/useFollowUser";
import { useFollowCheck } from "../features/managers/hooks/useFollowCheck";
import { useFollowProject } from "../features/managers/hooks/useFollowProject";
import { useProjectsFollowCheck } from "../features/managers/hooks/useProjectsFollowCheck";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/Tabs";


export default function Managers() {
  const { id: activeId } = useParams();
  const navigate = useNavigate();
  const managerId = activeId ? Number(activeId) : 0;
  const introRef = useRef<HTMLDivElement>(null);
  const worksRef = useRef<HTMLDivElement>(null);
  const [isLogoViewerOpen, setIsLogoViewerOpen] = useState(false);
  const [isIntroOverflowing, setIsIntroOverflowing] = useState(false);
  const [isWorksOverflowing, setIsWorksOverflowing] = useState(false);
  useEffect(() => {
    if (managerId === 9) {
      navigate("/founder", { replace: true });
    }
  }, [managerId, navigate]);
  const { data: person, isLoading: personLoading } = useSinglePerson(managerId);
  const personData = person?.data;
  const safePerson = {
    id: personData?.id ?? managerId ?? 0,
    name: personData?.name ?? "",
    nickname: personData?.nickname ?? "",
    layout: personData?.layout ?? "",
    logo: Array.isArray(personData?.logo)
      ? personData.logo[0]
      : (personData?.logo ?? ""),
    introduction: personData?.introduction ?? "",
    works: personData?.works ?? "",
    images: personData?.images ?? [],
    projects: personData?.projects ?? [],
    news: personData?.news ?? [],
    is_following: personData?.is_following ?? false,
  } as any;
  const projects = safePerson.projects ?? [];
  const personNews = mapPersonNewsToNews(safePerson, safePerson.news);
  const isIntroEmpty = isEmptyContent(safePerson.introduction);
  const isWorksEmpty = isEmptyContent(safePerson.works);
  const projectBoxPerson = {
    ...safePerson,
    projects:
      projects.length > 0
        ? projects
        : [
            {
              id: safePerson.id ?? managerId ?? 0,
              name: "",
              logo: "",
              categories: [],
              meta_text: "",
            },
          ],
  } as any;

  const compactProject = projectBoxPerson.projects?.[0];
  const galleryImages =
    safePerson.images?.map((img: string, i: number) => ({
      id: i,
      url: img,
      caption: "",
    })) || [];
  const { data: checkedUserFollowing, isFetching: userCheckLoading } =
    useFollowCheck("persons", managerId);
  const lastScrollY = useRef(0);
  const [showSticky, setShowSticky] = useState(false);
  const [activeTab, setActiveTab] = useState("introduction");
  const hasProject = safePerson.projects?.length > 0;
  const hasGallery = (safePerson.images?.length ?? 0) > 0;
  const hasSidebar = hasProject || hasGallery;
  const { followStatusMap, loadingMap: projectCheckLoadingState } =
    useProjectsFollowCheck(projects);
  const { toggleFollowUser, loading } = useFollowUser();
  const { toggleFollowProject } = useFollowProject();
  const [projectFollowOverrides, setProjectFollowOverrides] = useState<
    Record<number, boolean>
  >({});

  const [projectLoadingState, setProjectLoadingState] = useState<
    Record<number, boolean>
  >({});

  const [isFollowing, setIsFollowing] = useState<boolean>(false);
  const [expandedTab, setExpandedTab] = useState<
    "introduction" | "content" | null
  >(null);
  const expandTab = (tab: "introduction" | "content") => {
    setExpandedTab(tab);
  };
  const isIntroExpanded = expandedTab === "introduction";
  const isWorksExpanded = expandedTab === "content";
  useLayoutEffect(() => {
    if (activeTab === 'introduction' && introRef.current) {
      const el = introRef.current;
      setIsIntroOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [activeTab, safePerson.introduction, isIntroEmpty]);

  useLayoutEffect(() => {
    if (activeTab === 'content' && worksRef.current) {
      const el = worksRef.current;
      setIsWorksOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [activeTab, safePerson.works, isWorksEmpty]);
  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1280;
      if (!isMobile) {
        setShowSticky(false);
        lastScrollY.current = 0;
        return;
      }
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;
      const goingDown = diff > 0;
      const goingUp = diff < 0;
      const MIN_SCROLL = 10;

      if (goingDown && currentY > 120) {
        setShowSticky(true);
      }

      if (goingUp && Math.abs(diff) > MIN_SCROLL) {
        setShowSticky(false);
      }

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof checkedUserFollowing === "boolean") {
      setIsFollowing(checkedUserFollowing);
      return;
    }

    setIsFollowing(!!safePerson.is_following);
  }, [checkedUserFollowing, safePerson.is_following]);

  useEffect(() => {
    setProjectFollowOverrides({});
    setProjectLoadingState({});
  }, [managerId]);

  const handleToggleFollowUser = async () => {
    if (!activeId) return;

    const res = await toggleFollowUser(Number(activeId));

    if (!res) return;

    setIsFollowing(res.isFollowing);
  };

  const handleToggleFollowProject = async (projectId: number) => {
    if (!projectId) return;

    setProjectLoadingState((prev) => ({ ...prev, [projectId]: true }));

    const res = await toggleFollowProject(projectId);

    setProjectLoadingState((prev) => ({ ...prev, [projectId]: false }));

    if (!res) return;

    setProjectFollowOverrides((prev) => ({
      ...prev,
      [projectId]: res.isFollowing,
    }));
  };

  const getProjectFollowState = (projectId: number) => {
    if (
      Object.prototype.hasOwnProperty.call(projectFollowOverrides, projectId)
    ) {
      return projectFollowOverrides[projectId];
    }

    return followStatusMap[projectId] ?? false;
  };

  // 🌟 شرط گارد: جلوگیری از لود بقیه کامپوننت یا فلیکر تصویر در حین تغییر مسیر مدیر ۹
  if (managerId === 9) {
    return null;
  }

  const isLoading = personLoading;

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-green-01">
        <div className="flex items-center justify-center w-full h-screen">
          <div className="size-8 border-2 border-green-03 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-between min-h-screen bg-green-01">
      <div className="xl:flex xl:flex-row flex flex-col-reverse w-full items-start justify-center gap-x-4 px-4 pb-4 xl:pb-0 2xl:px-11 xl:my-5">
        {/* --- Aside Section --- */}
        {hasSidebar && (
          <aside className="w-full xl:w-[25%] 2xl:w-[19%] space-y-2">
            {hasProject && (
              <ProjectFollowBox
                person={projectBoxPerson}
                isFollowing={
                  projectBoxPerson.projects?.[0]?.id
                    ? getProjectFollowState(projectBoxPerson.projects[0].id)
                    : false
                }
                loading={
                  projectBoxPerson.projects?.[0]?.id
                    ? projectLoadingState[projectBoxPerson.projects[0].id] ||
                      projectCheckLoadingState[projectBoxPerson.projects[0].id]
                    : false
                }
                onToggleFollow={() =>
                  projectBoxPerson.projects?.[0]?.id &&
                  handleToggleFollowProject(projectBoxPerson.projects[0].id)
                }
                mode="desktop"
              />
            )}

            {hasGallery && (
              <div className="hidden xl:block">
                <ProjectGallery images={galleryImages} />
              </div>
            )}
          </aside>
        )}

        {/* --- Main Section --- */}
        <main
          className={`w-full ${
            hasSidebar ? "xl:w-[75%] 2xl:w-[81%]" : "xl:w-full 2xl:w-full"
          }`}
        >
          {/* ---------------- Desktop Header ---------------- */}
          <div
            className={`hidden xl:flex items-center justify-between bg-green-03 shadow-sm p-2.5 overflow-hidden rounded-[20px]
            transition-all duration-300 ease-in-out
            ${
              showSticky
                ? "opacity-0 -translate-y-6 scale-95 pointer-events-none"
                : "opacity-100 translate-y-0 scale-100"
            }`}
          >
            <div className="flex items-center justify-start gap-x-2.5">
              <div
                className="size-[122px] xl:size-[142px] shrink-0 border border-green-03 overflow-hidden rounded-2xl cursor-pointer"
                onClick={() => setIsLogoViewerOpen(true)}
              >
                <LazyImage
                  src={safePerson.logo || Logo}
                  alt="تصویر مدیر"
                  className="h-full w-full object-cover bg-green-02 text-xs text-center"
                />
              </div>

              <div className="flex flex-col justify-between gap-y-2 w-full ml-8 md:m-0">
                <span className="text-xs xl:text-[15px] font-normal text-white/60">
                  {safePerson.nickname}
                </span>

                <h2 className="text-base xl:text-xl text-white font-bold">
                  {safePerson.name}
                </h2>
              </div>
            </div>
            <Button
              onClick={handleToggleFollowUser}
              disabled={loading || userCheckLoading}
              className={`hidden xl:flex items-center justify-center gap-2 h-[40px] ml-12 !text-white transition ${
                isFollowing ? "bg-green-05/30 xl:hover:bg-green-05/20" : ""
              }`}
            >
              {loading || userCheckLoading ? (
                <span className="size-4 border-2 border-current border-t-transparent text-[15px] font-medium mx-5 rounded-full animate-spin" />
              ) : isFollowing ? (
                "دنبال میکنید"
              ) : (
                "دنبال کردن"
              )}
            </Button>
          </div>
          {/* ---------------- Mobile Header ---------------- */}
          <div
            className={`xl:hidden bg-green-03 shadow p-2.5 overflow-hidden ${
              hasProject ? "rounded-t-[20px]" : "rounded-[20px]"
            }`}
          >
            <div className="flex items-center justify-start gap-x-2.5">
              <div
                className="size-[122px] xl:size-[142px] shrink-0 border border-green-03 overflow-hidden rounded-2xl cursor-pointer"
                onClick={() => setIsLogoViewerOpen(true)}
              >
                <LazyImage
                  src={safePerson.logo || Logo}
                  alt="تصویر مدیر"
                  className="h-full w-full object-cover bg-green-02 text-xs text-center"
                />
              </div>

              <div className="flex flex-col justify-between gap-y-2.5 w-full ml-3.5">
                <div className="w-full">
                  <span className="text-xs font-medium text-white/55">
                    {safePerson.nickname}
                  </span>

                  <h2 className="text-base text-white font-bold">
                    {safePerson.name}
                  </h2>
                  <h2
                    className="text-xs font-medium text-white/55 line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html: safePerson.layout || "",
                    }}
                  />
                </div>

                <Button
                  onClick={handleToggleFollowUser}
                  disabled={loading || userCheckLoading}
                  className={`w-full md:w-fit xl:w-full flex items-center justify-center gap-2 text-xs font-normal transition rounded-[10px] ${
                    isFollowing
                      ? "bg-green-02 hover:bg-green-02 text-green-04"
                      : ""
                  }`}
                >
                  {loading || userCheckLoading ? (
                    <span className="size-4 border-2 border-current border-t-transparent mx-5 rounded-full animate-spin" />
                  ) : isFollowing ? (
                    "دنبال میکنید"
                  ) : (
                    "دنبال کردن"
                  )}
                </Button>
              </div>
            </div>
          </div>
          {hasProject && (
            <ProjectFollowBox
              person={projectBoxPerson}
              isFollowing={
                projectBoxPerson.projects?.[0]?.id
                  ? getProjectFollowState(projectBoxPerson.projects[0].id)
                  : false
              }
              loading={
                projectBoxPerson.projects?.[0]?.id
                  ? projectLoadingState[projectBoxPerson.projects[0].id] ||
                    projectCheckLoadingState[projectBoxPerson.projects[0].id]
                  : false
              }
              onToggleFollow={() =>
                projectBoxPerson.projects?.[0]?.id &&
                handleToggleFollowProject(projectBoxPerson.projects[0].id)
              }
              mode="mobile"
            />
          )}
          <div
            className={`fixed top-0 left-0 right-0 z-30 transition-all duration-500 ease-in-out
            ${
              showSticky
                ? "opacity-100 translate-y-0"
                : "opacity-0 -translate-y-10 pointer-events-none"
            }`}
          >
            <div className="w-full bg-green-02/40 backdrop-blur-xl px-2.5 h-[72px] flex items-center justify-between pl-8 gap-3">
              <div className="flex items-center justify-center gap-x-2.5">
                <LazyImage
                  src={safePerson.logo || Logo}
                  alt="تصویر مدیر"
                  onError={(e) => {
                    e.currentTarget.src = Logo;
                  }}
                  className="size-10 border border-green-02 object-cover shrink-0 bg-white rounded-xl xl:rounded-full"
                />
                <div className="flex-col space-y-1">
                  <h2 className="text-[10px] text-green-04 font-medium">
                    {safePerson.nickname}
                  </h2>
                  <h3 className="text-xs font-bold text-green-05 truncate">
                    {safePerson.name}
                  </h3>
                </div>
              </div>
              <Link
                to={`/institutions/${compactProject?.id}`}
                className="flex items-center gap-x-2.5 min-w-0"
              >
                <LazyImage
                  src={compactProject?.logo || Logo}
                  alt="لوگوی مجموعه"
                  className="size-10 border border-green-02 object-cover shrink-0 bg-white rounded-full xl:rounded-xl"
                />

                <p className="text-[9px] text-wrap line-clamp-2 font-bold text-green-04 truncate">
                  {compactProject?.name || ""}
                </p>
              </Link>
            </div>
          </div>
          {/* --- Tabs --- */}
          <div className="transition-all duration-300 ease-in-out mt-2.5">
            <Tabs onValueChange={setActiveTab} defaultValue="introduction">
              <div className="w-full transition-all duration-300 ease-in-out">
                <TabsList className="w-full h-11 !justify-center bg-white shadow-sm rounded-2xl">
                  <div className="flex items-center justify-center gap-x-6">
                    {/* <TabsTrigger
                      value="news"
                      className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                    >
                      اخبار
                    </TabsTrigger> */}
                    <TabsTrigger
                      value="content"
                      className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                    >
                      دستاوردها
                    </TabsTrigger>

                    <TabsTrigger
                      value="introduction"
                      className="!h-7 text-[13px] text-green-05 data-[state=active]:bg-green-01 data-[state=active]:text-green-05 font-medium !border-none px-2 shadow-none !rounded-lg"
                    >
                      معرفی
                    </TabsTrigger>
                  </div>
                </TabsList>
              </div>
              <div
                className={`w-full
                  ${hasGallery ? "rounded-t-[20px]" : "rounded-[20px]"} 
                  md:rounded-[20px] 
                  overflow-hidden`}
              >
                <TabsContent
                  value="introduction"
                  dir="rtl"
                  className={`relative bg-white ${isIntroEmpty ? "p-0" : "p-4 xl:p-9"} min-h-fit ${hasGallery ? "rounded-t-[20px]" : "rounded-[20px]"} overflow-hidden xl:rounded-xl`}
                >
                  {isIntroEmpty ? (
                    <EmptyState />
                  ) : (
                    <div
                      ref={introRef}
                      className={`text-sm estedad3 leading-7 transition-all duration-300 ${
                        !isIntroExpanded ? "max-h-[13rem] overflow-hidden" : ""
                      }`}
                    >
                      <p
                        dangerouslySetInnerHTML={{
                          __html: safePerson.introduction || "",
                        }}
                      />
                    </div>
                  )}
                  {!isIntroExpanded && !isIntroEmpty && isIntroOverflowing && (
                    <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                  )}

                  {!isIntroExpanded && !isIntroEmpty && isIntroOverflowing && (
                    <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                      <button
                        onClick={() => expandTab("introduction")}
                        className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                      >
                        بیشتر
                      </button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="news"
                  dir="rtl"
                  className="rounded-[20px] xl:rounded-xl"
                >
                  <NewsSection
                    form="form1"
                    layout="grid"
                    showHeader={false}
                    showEmptyState
                    newsData={personNews ?? []}
                  />
                </TabsContent>

                <TabsContent
                  value="content"
                  dir="rtl"
                  className={`relative bg-white ${isWorksEmpty ? "p-0" : "p-4 xl:p-9"} min-h-fit rounded-[20px] overflow-hidden xl:rounded-xl`}
                >
                  {isWorksEmpty ? (
                    <EmptyState />
                  ) : (
                    <div
                      ref={worksRef}
                      className={`text-sm estedad3 leading-7 transition-all duration-300 ${
                        !isWorksExpanded ? "max-h-[13rem] overflow-hidden" : ""
                      }`}
                    >
                      <p
                        dangerouslySetInnerHTML={{
                          __html: safePerson.works || "",
                        }}
                      />
                    </div>
                  )}

                  {!isWorksExpanded && !isWorksEmpty && isWorksOverflowing && (
                    <div className="absolute bottom-0 left-0 w-full h-28 bg-gradient-to-t from-white via-white/85 to-transparent" />
                  )}

                  {!isWorksExpanded && !isWorksEmpty && isWorksOverflowing && (
                    <div className="absolute bottom-4 left-0 w-full flex justify-center z-10">
                      <button
                        onClick={() => expandTab("content")}
                        className="text-green-04 text-[10px] px-3 h-7 bg-green-02 rounded-lg shadow-sm"
                      >
                        بیشتر
                      </button>
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
          {hasGallery && activeTab === "introduction" && (
            <div className="md:hidden xl:mt-4 bg-white shadow-sm rounded-b-[20px] xl:rounded-2xl overflow-hidden">
              <ProjectGallery images={safePerson.images || []} />
            </div>
          )}
          {isLogoViewerOpen &&
            createPortal(
              <div
                className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
                onClick={() => setIsLogoViewerOpen(false)}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsLogoViewerOpen(false);
                  }}
                  className="absolute top-4 right-4 size-8 rounded-full bg-white/10 text-white text-xl flex items-center justify-center z-20"
                >
                  ×
                </button>

                <img
                  src={safePerson.logo || Logo}
                  alt="تصویر مدیر"
                  className="max-w-full max-h-[90vh] object-contain rounded-2xl select-none"
                  draggable={false}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>,
              document.body,
            )}
        </main>
      </div>
    </div>
  );
}
